<div class="tcm">

  <!-- ------------------------------------------------------------- header -->
  <mat-card>
    <mat-card-content>
      <div class="mat-card-content-label" fxLayoutAlign="space-between center" fxLayout="row"
           fxLayout.lt-sm="column" fxLayoutGap="0px" fxLayoutGap.lt-sm="15px">
        <label>{{ headerTitle }}</label>
        <span class="tcm-head-actions">
          <span class="tcm-pending" *ngIf="pendingTotal && canApprove">
            <mat-icon>hourglass_empty</mat-icon>
            {{ pendingTotal }} awaiting your approval
          </span>
          <!-- The URL tracks the partner, product and open tile, so this link
               opens exactly this view, for either side of the partnership. -->
          <button mat-stroked-button *ngIf="selectedProductId" (click)="copyLink()"
                  matTooltip="Copy a link to this product and tile">
            <mat-icon>link</mat-icon> Copy link
          </button>
        </span>
      </div>

      <mat-card [ngClass.lt-sm]="{'mat-card-inline-sm': true}">
        <mat-card-content>
          <div class="filter" fxLayoutAlign="start center" fxLayout="row" fxLayoutGap="15px"
               fxLayout.lt-sm="column">
            <mat-form-field>
              <mat-label>{{ partnerLabel }}</mat-label>
              <mat-select [(ngModel)]="selectedPartnerId" (selectionChange)="onPartnerChange()"
                          [disabled]="isLoading || !partners.length">
                <mat-option *ngFor="let p of partners" [value]="p.id">{{ p.name }}</mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field class="tcm-product-field">
              <mat-label>Product</mat-label>
              <mat-select [(ngModel)]="selectedProductId" (selectionChange)="onProductChange()"
                          [disabled]="isLoading || !products.length">
                <mat-option *ngFor="let p of products" [value]="p.id">{{ p.name }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Say plainly whose surface this is. As a manufacturer the picker is
           choosing which builder's website you are previewing; as a builder it
           is choosing whose content you are reviewing for your own. -->
      <div class="tcm-context" [class.tcm-context-builder]="isApprovalMode">
        <mat-icon>{{ !isApprovalMode ? 'storefront' : 'fact_check' }}</mat-icon>
        <div>
          <ng-container *ngIf="!isApprovalMode">
            <strong>This is how your product appears on {{ selectedPartnerName }}'s website.</strong>
            Everything you add here goes to {{ selectedPartnerName }} for approval before their buyers see it.
          </ng-container>
          <ng-container *ngIf="isApprovalMode">
            <strong>{{ selectedPartnerName }}'s content, on your surfaces.</strong>
            Approve what you want live on {{ selectedCompany?.name }}'s website. Nothing appears to your buyers
            until you approve it.
          </ng-container>
        </div>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- -------------------------------------------------------------- error -->
  <div class="tcm-error" *ngIf="error">
    <mat-icon>error_outline</mat-icon>
    <span>{{ error }}</span>
    <button mat-button (click)="dismissError()">Dismiss</button>
  </div>

  <!-- ------------------------------------------------------------ loading -->
  <mat-card class="tcm-empty" *ngIf="isLoading">
    <mat-card-content>Loading content…</mat-card-content>
  </mat-card>

  <mat-card class="tcm-empty" *ngIf="!isLoading && !partners.length">
    <mat-card-content>
      <ng-container *ngIf="isApprovalMode">No supplier partnerships, so there is no content to approve.</ng-container>
      <ng-container *ngIf="!isApprovalMode">No active partnerships, so there is nothing to approve against.</ng-container>
    </mat-card-content>
  </mat-card>

  <!-- --------------------------------------------------------------- body -->
  <ng-container *ngIf="!isLoading && partners.length">
    <mat-card class="tcm-empty" *ngIf="!products.length">
      <mat-card-content>
        {{ supplierName }} has no products in the catalogue, so there are no product tiles to fill.
      </mat-card-content>
    </mat-card>

    <div class="tcm-cols" *ngIf="products.length">

      <!-- ------------------------------------------------------ accordion -->
      <div class="tcm-left">
        <mat-card class="tcm-level" *ngFor="let level of levels; trackBy: trackByLevel"
                  [class.tcm-open]="openLevel === level.level">

          <div class="tcm-level-head" (click)="toggleLevel(level.level)">
            <mat-icon class="tcm-chev">{{ openLevel === level.level ? 'expand_more' : 'chevron_right' }}</mat-icon>
            <div class="tcm-level-titles">
              <div class="tcm-level-name">{{ levelTitles[level.level] }} tile</div>
              <div class="tcm-level-blurb">{{ levelBlurbs[level.level] }}</div>
            </div>
            <div class="tcm-level-meter">
              <span class="tcm-chip tcm-chip-pending" *ngIf="level.pending">{{ level.pending }} pending</span>
              <span class="tcm-chip" *ngIf="level.level !== 'product'"
                    [ngClass]="level.live ? 'tcm-chip-live' : 'tcm-chip-hidden'">
                {{ level.live ? 'Live' : 'Not live yet' }}
              </span>
              <div class="tcm-bar-track">
                <div class="tcm-bar-fill" [style.width.%]="(level.filled / level.total) * 100"></div>
              </div>
              <span class="tcm-count">{{ level.filled }} of {{ level.total }}</span>
            </div>
          </div>

          <div class="tcm-slots" *ngIf="openLevel === level.level">
            <div class="tcm-slot" *ngFor="let slot of level.slots; trackBy: trackBySlot"
                 [class.tcm-lit]="highlightedSlot === slot.n"
                 (mouseenter)="highlight(slot.n)" (mouseleave)="highlight(null)">

              <span class="tcm-num">{{ slot.n }}</span>

              <div class="tcm-slot-meta">
                <div class="tcm-slot-label">
                  {{ slot.label }}
                  <span class="tcm-chip tcm-chip-override" *ngIf="slot.partnerOverride">partner override</span>
                </div>
                <div class="tcm-slot-value" [class.tcm-slot-empty]="!slot.value">
                  {{ slot.value || 'not set' }}
                </div>
                <div class="tcm-slot-note" *ngIf="slot.note">{{ slot.note }}</div>
                <div class="tcm-slot-queued" *ngIf="previewNotice && slot.label === 'Product render'">
                  {{ previewNotice }}
                </div>

                <!-- Inline editor. Text for catalogue copy, a picker for the range. -->
                <div class="tcm-edit" *ngIf="editing === slot.label">
                  <ng-container *ngIf="slot.label === 'Range'; else textEdit">
                    <mat-form-field appearance="outline" class="tcm-edit-field">
                      <mat-label>Range</mat-label>
                      <mat-select [value]="currentBrandId" (selectionChange)="assignBrand($event.value)">
                        <mat-option [value]="null">No range</mat-option>
                        <mat-option *ngFor="let b of brands" [value]="b.id">{{ b.name }}</mat-option>
                      </mat-select>
                    </mat-form-field>
                    <button mat-button (click)="cancelEdit()">Cancel</button>
                  </ng-container>

                  <ng-template #textEdit>
                    <mat-form-field appearance="outline" class="tcm-edit-field tcm-edit-field-wide">
                      <mat-label>{{ slot.label }}</mat-label>
                      <textarea matInput rows="2" [(ngModel)]="draftText"
                                [placeholder]="'Write the ' + slot.label.toLowerCase()"></textarea>
                    </mat-form-field>
                    <button mat-flat-button color="primary" [disabled]="isBusy"
                            (click)="saveEdit(slot)">Save</button>
                    <button mat-button (click)="cancelEdit()">Cancel</button>
                  </ng-template>
                </div>
              </div>

              <span class="tcm-state" [ngClass]="'tcm-state-' + slot.state">{{ slot.state }}</span>

              <div class="tcm-slot-actions">
                <!-- Approving is the builder's job; editing and uploading are the
                     supplier's. Each button is gated on direction and role. -->
                <button mat-flat-button color="primary" class="tcm-act"
                        *ngIf="canApprove && (slot.state === 'pending' || slot.state === 'rejected')"
                        [disabled]="isBusy || isRefreshing" (click)="decide(slot, true)">Approve</button>
                <button mat-stroked-button class="tcm-act"
                        *ngIf="canApprove && (slot.state === 'pending' || slot.state === 'approved')"
                        [disabled]="isBusy || isRefreshing" (click)="decide(slot, false)">Reject</button>

                <!-- Editable catalogue text -->
                <button mat-stroked-button class="tcm-act"
                        *ngIf="canEdit && !slot.noFieldYet && editing !== slot.label
                               && (slot.label === 'Description' || slot.label === 'Product name' || slot.label === 'SKU')"
                        [disabled]="isBusy || isRefreshing" (click)="startEdit(slot)">
                  {{ slot.value ? 'Edit' : 'Add' }}
                </button>

                <!-- Range assignment -->
                <button mat-stroked-button class="tcm-act"
                        *ngIf="canEdit && slot.label === 'Range' && editing !== slot.label"
                        [disabled]="isBusy || isRefreshing" (click)="startEdit(slot)">
                  {{ slot.value ? 'Change' : 'Assign' }}
                </button>

                <!-- Queue the auto-generated render when there is none -->
                <button mat-stroked-button class="tcm-act"
                        *ngIf="canGeneratePreview && slot.label === 'Product render' && slot.state === 'missing'"
                        [disabled]="isBusy || isRefreshing" (click)="generatePreview()">
                  Generate
                </button>

                <!-- Upload into a role slot -->
                <button mat-stroked-button class="tcm-act"
                        *ngIf="canUploadToSlot(slot)"
                        [disabled]="isBusy || isRefreshing" (click)="slotFile.click()">
                  {{ slot.value ? 'Replace' : 'Upload' }}
                  <input #slotFile type="file" class="tcm-file-input" hidden
                         [accept]="acceptForSlot(slot)"
                         (change)="uploadForSlot(slot, $any($event.target).files)"
                         onclick="value = null" />
                </button>

                <span class="tcm-nofield" *ngIf="slot.noFieldYet">no field yet</span>
              </div>
            </div>

            <div class="tcm-bulk" *ngIf="level.pending && canApprove">
              <button mat-flat-button color="primary" [disabled]="isBusy" (click)="approveAllPending()">
                Approve all {{ level.pending }} pending on this tile
              </button>
            </div>
          </div>
        </mat-card>
      </div>

      <!-- ------------------------------------------------------ schematic -->
      <div class="tcm-right">
        <mat-card class="tcm-preview">
          <mat-card-content>
            <div class="tcm-preview-head">
              {{ levelTitles[openLevel] }} tile
              <span class="tcm-refreshing" *ngIf="isRefreshing">updating…</span>
            </div>

            <!-- The live SituReach tile, composed from this product and only
                 what this partner has approved. `fit: true` scales the native
                 540x1080 into the panel. -->
            <div class="tcm-tile-host" #tileHost></div>

            <p class="tcm-preview-note tcm-preview-warn" *ngIf="openSummary && !openSummary.live">
              Buyers don't see this tile yet. It goes live once every item on it is approved:
              {{ openSummary.filled }} of {{ openSummary.total }} so far.
            </p>

            <p class="tcm-preview-note tcm-preview-warn" *ngIf="openLevelIsFallback">
              This product has no range, so the tile falls back to the supplier catalogue — which is why it looks
              like the Company tile. A showcase would skip this level entirely.
            </p>

            <p class="tcm-preview-note">
              The real tile, as <strong>{{ selectedPartnerName }}</strong> renders it. Unapproved material is
              deliberately absent — approve a slot on the left and it appears here.
            </p>
          </mat-card-content>
        </mat-card>
      </div>
    </div>
  </ng-container>
</div>
